import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

export interface FieldSearchConfig {
  fieldName: string;
  label: string;
  inputType?: string;
  placeholder?: string;
}

interface FieldSearchProps {
  fields: FieldSearchConfig[];
  defaultFieldName?: string;
  onSearch: (values: Record<string, string>) => void;
  buttonLabel?: string;
  clearLabel?: string;
}

const FieldSearch: React.FC<FieldSearchProps> = ({
  fields,
  defaultFieldName,
  onSearch,
  buttonLabel = "Search",
  clearLabel = "Clear",
}) => {
  const [selectedFieldName, setSelectedFieldName] = React.useState(
    defaultFieldName || fields[0]?.fieldName || ""
  );
  const [value, setValue] = React.useState("");

  const selectedField = fields.find((field) => field.fieldName === selectedFieldName);

  const handleFieldChange = (event: SelectChangeEvent<string>) => {
    setSelectedFieldName(event.target.value);
    setValue("");
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedValue = value.trim();
    onSearch(trimmedValue ? { [selectedFieldName]: trimmedValue } : {});
  };

  const handleClear = () => {
    setValue("");
    setSelectedFieldName(defaultFieldName || fields[0]?.fieldName || "");
    onSearch({});
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
      }}
    >
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <InputLabel id="field-search-select-label">Search By</InputLabel>
        <Select
          labelId="field-search-select-label"
          label="Search By"
          value={selectedFieldName}
          onChange={handleFieldChange}
        >
          {fields.map((field) => (
            <MenuItem key={field.fieldName} value={field.fieldName}>
              {field.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        size="small"
        type={selectedField?.inputType || "text"}
        placeholder={selectedField?.placeholder || `Search by ${selectedField?.label || "value"}`}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        sx={{ width: 300, maxWidth: "100%" }}
      />
      <Button
        type="submit"
        variant="outlined"
        startIcon={<SearchIcon />}
        disabled={!selectedFieldName}
      >
        {buttonLabel}
      </Button>
      <Button
        type="button"
        variant="text"
        startIcon={<ClearIcon />}
        onClick={handleClear}
      >
        {clearLabel}
      </Button>
    </Box>
  );
};

export default FieldSearch;
